import React, { useState, useMemo, useEffect } from "react";
import { ToolShell, Field, Select, fmt, SpecCard, SpecValue } from "./_shared";

/**
 *  WATER-SOFTENING RESIN SIZING — v4 (single chain of truth)
 *  --------------------------------------------------------------
 *  Engineering pipeline — exactly the way an operator/procurement
 *  officer thinks about it:
 *
 *      daily demand + hardness  ─►  hardness load per cycle (g CaCO₃)
 *      salt-efficiency target   ─►  working capacity (g CaCO₃/L)
 *                                ─►  resin volume (L, kg)
 *                                ─►  salt per regen  (kg NaCl)
 *                                ─►  brine per regen (L)  → brine tank size
 *      regen method (either/or):
 *          • Eductor head (Runxin / Fleck / Clack):  brine-draw time
 *            = brine ÷ head draw-rate
 *          • Direct chemical dosing:  brine-dosing time
 *            = brine ÷ dosing-pump rate
 *      cycle time = backwash + brine + slow rinse + fast rinse + refill
 *      offline check = does cycle fit (24 − service hours)?  If not →
 *                       suggest larger head, more frequent cycles, or
 *                       duplex / twin-alternating duty.
 *
 *  Trigger mode (also either/or, like the user asked):
 *      • Regen by TIME       — fixed hours between cycles
 *      • Regen by VOLUME     — fixed litres treated per cycle
 *
 *  Comparison table re-shapes depending on trigger mode:
 *      • time   → compare 12 / 24 / 36 / 48 / 72 h intervals
 *      • volume → compare 25 / 50 / 100 / 150 / 200 % of daily demand
 *
 *  Hard floor: salt efficiency < 2.0 g CaCO₃/g NaCl is rejected — that's
 *  worse than the worst real-world softener.
 * --------------------------------------------------------------
 *  Constants used:
 *      1 mEq/L CaCO₃   = 50 mg/L CaCO₃
 *      Saturated brine ≈ 320 g NaCl / L
 *      Na added        ≈ 0.46 × hardness removed (mg/L CaCO₃)
 *      Resin density   ≈ 0.85 kg/L (SAC bulk)
 *
 *  Vendor curve fit (g CaCO₃/L resin) ≈ 65 − 7·(salt eff g/g)
 *      eff 2.5 → 47 g/L · eff 3.0 → 44 · eff 4.0 → 37 ·
 *      eff 5.0 → 30 · eff 6.0 → 23
 * --------------------------------------------------------------
 */

/* ────────────────────────────────────────────────────────────── */
/*  Reference data                                                */
/* ────────────────────────────────────────────────────────────── */
const SALT_EFF_PRESETS = [
  { value: 2.5, label: "2.5 g/g  ·  High-capacity (low salt economy, big cycles)" },
  { value: 3.0, label: "3.0 g/g  ·  Utility default" },
  { value: 4.0, label: "4.0 g/g  ·  Standard hospitality / commercial" },
  { value: 5.0, label: "5.0 g/g  ·  High efficiency (lean salt budget)" },
  { value: 5.5, label: "5.5 g/g  ·  Very high efficiency" },
];

// Runxin published injector draw rates @ 0.30 MPa — nominal, verify per eductor colour.
const VALVE_HEADS = [
  { value: "f69q-6301",  brand: "Runxin", label: "Runxin F69Q · 6301 Coffee · 205 mm tank (~25 L resin)",  drawLpm: 1.76 },
  { value: "f69q-6303",  brand: "Runxin", label: "Runxin F69Q · 6303 Yellow · 255 mm tank (~40 L resin)",  drawLpm: 3.20 },
  { value: "f69q-6304",  brand: "Runxin", label: "Runxin F69Q · 6304 Blue · 300 mm tank (~60 L resin)",    drawLpm: 4.08 },
  { value: "f68q-6306",  brand: "Runxin", label: "Runxin F68Q · 6306 Black · 355 mm tank (~100 L resin)",  drawLpm: 5.59 },
  { value: "f68q-6307",  brand: "Runxin", label: "Runxin F68Q · 6307 Purple · 400 mm tank (~120 L resin)", drawLpm: 6.60 },
  { value: "f68q-6308",  brand: "Runxin", label: "Runxin F68Q · 6308 Red · 450 mm tank (~150 L resin)",    drawLpm: 7.32 },
  { value: "f117q-6303", brand: "Runxin", label: "Runxin F117Q · 6303 Yellow · 175–205 mm tank",           drawLpm: 2.75 },
  { value: "f117q-6304", brand: "Runxin", label: "Runxin F117Q · 6304 Blue · 225–255 mm tank",             drawLpm: 3.33 },
  { value: "f117q-6307", brand: "Runxin", label: "Runxin F117Q · 6307 Purple · 300–355 mm tank",           drawLpm: 4.27 },
  { value: "f116q-6306", brand: "Runxin", label: "Runxin F116Q · 6306 Black · 255–300 mm tank",            drawLpm: 5.27 },
  { value: "f116q-6308", brand: "Runxin", label: "Runxin F116Q · 6308 Red · 325–355 mm tank",              drawLpm: 6.80 },
  { value: "f116q-6309", brand: "Runxin", label: "Runxin F116Q · 6309 Green · 400–450 mm tank",            drawLpm: 8.10 },
  { value: "f77-nom",    brand: "Runxin", label: "Runxin F77 (1½″) · industrial · ~ 10 L/min nominal",     drawLpm: 10.0 },
  { value: "f96-nom",    brand: "Runxin", label: "Runxin F96 (2″) · industrial · ~ 15 L/min nominal",      drawLpm: 15.0 },
  { value: "f112-nom",   brand: "Runxin", label: "Runxin F112 (3″) · industrial · ~ 25 L/min nominal",     drawLpm: 25.0 },
  { value: "custom",     brand: "Custom", label: "Other / custom — enter L/min below",                     drawLpm: 4.0  },
];

const TRIGGER_MODES = [
  { value: "time",   label: "Regenerate every N hours (time-based)" },
  { value: "volume", label: "Regenerate every N litres treated (volume-based)" },
];

const REGEN_METHODS = [
  { value: "eductor", label: "Eductor head — brine drawn from a saturated brine tank" },
  { value: "dosing",  label: "Direct chemical dosing — pump injects brine into the column" },
];

const TIME_PRESETS = [
  { value: "12",     label: "Every 12 h (twin alternating duty)" },
  { value: "24",     label: "Every 24 h (single, overnight regen)" },
  { value: "36",     label: "Every 36 h" },
  { value: "48",     label: "Every 48 h" },
  { value: "72",     label: "Every 72 h (low-load installs)" },
  { value: "custom", label: "Custom (enter hours below)" },
];

const TIME_COMPARE_HOURS = [12, 24, 36, 48, 72];
// As fractions of daily demand for volume-mode comparison.
const VOLUME_COMPARE_FRACTIONS = [0.25, 0.5, 1.0, 1.5, 2.0];

const BRINE_STRENGTH = 320;  // g NaCl / L saturated brine
const RESIN_DENSITY  = 0.85; // kg / L SAC bulk

// Working-capacity curve as a function of salt efficiency.
function workingCapacityFromEff(eff) {
  const v = 65 - 7 * eff;
  return Math.max(15, Math.min(60, v));
}

/* ────────────────────────────────────────────────────────────── */
/*  Core engineering function                                     */
/* ────────────────────────────────────────────────────────────── */
function sizeSoftener({
  cycleVolumeL, hardRemoved_mgL, saltEff, safetyFactor,
  drawLpm, backwashMin, slowRinseMin, fastRinseMin, brineRefillMin,
  backwashLpm, slowRinseLpm, fastRinseLpm,
  serviceHours, regenHours, dailyL,
}) {
  // Step 1 — hardness load trapped per cycle (g CaCO₃)
  const loadPerCycle_g = cycleVolumeL * hardRemoved_mgL / 1000;

  // Step 2 — salt mass needed per cycle (kg NaCl)
  const saltPerRegen_kg = saltEff > 0 ? loadPerCycle_g / (saltEff * 1000) : 0;

  // Step 3 — brine volume (L of saturated brine)
  const brinePerRegen_L = saltPerRegen_kg * 1000 / BRINE_STRENGTH;

  // Step 4 — resin volume from capacity curve
  const workingCap     = workingCapacityFromEff(saltEff);
  const designLoad_g   = loadPerCycle_g * (1 + safetyFactor / 100);
  const resinVolume_L  = workingCap > 0 ? designLoad_g / workingCap : 0;
  const resinMass_kg   = resinVolume_L * RESIN_DENSITY;

  // Step 5 — brine draw time (eductor mode) — directly from draw rate
  const brineDrawMin = drawLpm > 0 ? brinePerRegen_L / drawLpm : 0;

  // Step 6 — rinse water = time × measured flow rate (L/min)
  // L/min is what an operator actually reads on a flow meter or picks
  // off a pump curve. BV/min is shown as a derived hint in the UI.
  const backwashL  = backwashMin  * backwashLpm;
  const slowRinseL = slowRinseMin * slowRinseLpm;
  const fastRinseL = fastRinseMin * fastRinseLpm;
  const rinsePerRegen_L = backwashL + slowRinseL + fastRinseL;

  // Step 7 — total regen cycle time (min and h)
  const totalRegenMin = (backwashMin || 0) + brineDrawMin + (slowRinseMin || 0)
                      + (fastRinseMin || 0) + (brineRefillMin || 0);
  const totalRegen_H  = totalRegenMin / 60;

  // Step 8 — offline window check
  const offlineWindowH   = Math.max(0, 24 - serviceHours);
  const regenFitsOffline = offlineWindowH > 0 && totalRegen_H <= offlineWindowH;

  // Step 9 — daily totals (regens per day)
  const regensPerDay = regenHours > 0 ? 24 / regenHours : 0;
  const dailySalt_kg = saltPerRegen_kg * regensPerDay;
  const dailyBrine_L = brinePerRegen_L * regensPerDay;
  const dailyRinse_L = rinsePerRegen_L * regensPerDay;
  const dailyWaste_L = dailyBrine_L + dailyRinse_L;

  // Step 10 — service rate sanity check (BV/h)
  const serviceFlow_Lh = serviceHours > 0 ? dailyL / serviceHours : 0;
  const bvPerHour      = resinVolume_L > 0 ? serviceFlow_Lh / resinVolume_L : 0;

  // Step 11 — brine tank min size (saturated brine + 20% headspace)
  const brineTankMinL = brinePerRegen_L * 1.20;

  return {
    cycleVolumeL, loadPerCycle_g, workingCap,
    saltPerRegen_kg, brinePerRegen_L, brineTankMinL,
    resinVolume_L, resinMass_kg,
    brineDrawMin, backwashL, slowRinseL, fastRinseL, rinsePerRegen_L,
    totalRegenMin, totalRegen_H,
    offlineWindowH, regenFitsOffline,
    regensPerDay, dailySalt_kg, dailyBrine_L, dailyRinse_L, dailyWaste_L,
    bvPerHour,
  };
}

/* ────────────────────────────────────────────────────────────── */
/*  UI helpers                                                    */
/* ────────────────────────────────────────────────────────────── */
function SuffixNum({ value, onChange, suffix, step = "any", min, testid }) {
  // Buffered text-mode input — fixes two UX bugs:
  //   (a) A field with value 2 wouldn't clear (controlled number input
  //       snapped empty → 0 → repaint as "0" before the user finished).
  //       We buffer the raw string locally so the field can sit empty.
  //   (b) "15 becomes 015":  parent state of 0 used to render as "0",
  //       then typing "15" produced "015". We treat 0 as the empty /
  //       placeholder state and never render a leading zero.
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        data-testid={testid}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                 fontSize: "0.95rem", fontWeight: 500 }}
      />
      <span
        className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)",
                 color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}
      >
        {suffix}
      </span>
    </div>
  );
}

function Stat({ label, value, unit, alt, status, highlight, testid }) {
  const colors = {
    ok:    { bg: "rgba(31,182,193,0.10)", border: "rgba(31,182,193,0.40)" },
    warn:  { bg: "rgba(194,146,46,0.10)", border: "rgba(194,146,46,0.40)" },
    alert: { bg: "rgba(184,92,56,0.10)",  border: "rgba(184,92,56,0.40)" },
  };
  const c = (status && colors[status])
    || (highlight ? colors.ok : { bg: "#FFFFFF", border: "rgba(12,42,64,0.10)" });
  return (
    <div className="px-4 py-3"
         style={{ background: c.bg, border: `1px solid ${c.border}` }}
         data-testid={testid}>
      <div className="font-mono uppercase"
           style={{ fontSize: "0.58rem", letterSpacing: "0.2em",
                    color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0C2A40" }}>{value}</span>
        <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {alt && (
        <div className="mt-1 font-light"
             style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>{alt}</div>
      )}
    </div>
  );
}

function SectionHeader({ n, label, topSpacing }) {
  return (
    <div className={topSpacing ? "mt-7 mb-3" : "mb-3"}>
      <div className="font-mono uppercase"
           style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                    color: "#0C2A40", fontWeight: 700 }}>
        {n} · {label}
      </div>
    </div>
  );
}

function Em({ children }) {
  return <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{children}</span>;
}

// Rinse-flow input — accepts L/min or m³/h (per flowUnit toggle),
// stores L/min internally, surfaces the BV/min equivalent for the
// current resin column so the operator can sanity-check against the
// SAC industry band without leaving the input.
function FlowField({ label, value, onChange, isAuto, flowUnit, resinL,
                    bvBand, purpose, testid }) {
  const factor = flowUnit === "lpm" ? 1 : 0.06;
  const suffix = flowUnit === "lpm" ? "L/min" : "m³/h";
  const displayed = value * factor;
  const bvPerMin  = resinL > 0 ? value / resinL : 0;
  const inBand = (() => {
    const m = (bvBand || "").match(/([0-9.]+)\s*[–-]\s*([0-9.]+)/);
    if (!m) return null;
    return bvPerMin >= parseFloat(m[1]) && bvPerMin <= parseFloat(m[2]);
  })();

  // Decimal-safe string buffer — see _shared.SuffixNum. We display the
  // unit-converted value (L/min ↔ m³/h) but type-buffer the raw text
  // so trailing dots and partial decimals aren't stripped by React.
  const externalStr = Number.isFinite(displayed) && displayed !== 0
    ? String(+displayed.toFixed(2))
    : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsedDisplayed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsedDisplayed)
      ? Math.abs(parsedDisplayed - (Number.isFinite(displayed) ? displayed : 0)) < 1e-6
      : (buf === "" && (!Number.isFinite(displayed) || displayed === 0));
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, flowUnit]);

  return (
    <div>
      <div className="font-mono uppercase mb-1"
           style={{ fontSize: "0.6rem", letterSpacing: "0.22em",
                    color: "rgba(12,42,64,0.65)", fontWeight: 700 }}>
        {label}
      </div>
      <div className="flex items-stretch">
        <input
          type="text"
          inputMode="decimal"
          value={buf}
          placeholder="0"
          onChange={(e) => {
            const raw = e.target.value;
            if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
            setBuf(raw);
            if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(null); return; }
            const n = parseFloat(raw.replace(",", "."));
            if (Number.isFinite(n)) onChange(n / factor);
          }}
          data-testid={testid}
          className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
          style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                   fontSize: "0.95rem", fontWeight: 500 }}
        />
        <span
          className="inline-flex items-center px-3 border border-l-0 font-mono"
          style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)",
                   color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}
        >
          {suffix}
        </span>
      </div>
      <div className="mt-1 font-light flex flex-wrap items-baseline gap-2"
           style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.6)",
                    lineHeight: 1.4 }}>
        <span style={{
          padding: "1px 6px", borderRadius: 3,
          background: inBand === null ? "rgba(12,42,64,0.06)"
                    : inBand ? "rgba(31,182,193,0.18)"
                    :          "rgba(194,146,46,0.18)",
          color: inBand === null ? "rgba(12,42,64,0.7)"
               : inBand ? "#0E7A82" : "#8A6914",
          fontWeight: 600, fontFamily: "ui-monospace, monospace",
          fontSize: "0.7rem",
        }}>
          ≈ {fmt(bvPerMin, 2)} BV/min
        </span>
        <span>SAC band {bvBand} BV/min</span>
        {isAuto && (
          <span className="font-mono uppercase"
                style={{ fontSize: "0.55rem", letterSpacing: "0.18em",
                         color: "rgba(31,182,193,0.85)", fontWeight: 700 }}>
            auto — scales with resin
          </span>
        )}
      </div>
      {purpose && (
        <div className="mt-1 font-light"
             style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)",
                      lineHeight: 1.45 }}>
          {purpose}
        </div>
      )}
    </div>
  );
}

// Radio-pair: two big either/or tiles, fully visual.
// Radio-pair: two big either/or tiles, fully visual.
function RadioPair({ value, onChange, options, testid }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid={testid}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            data-testid={`${testid}-${o.value}`}
            className="text-left px-4 py-3"
            style={{
              background: active ? "rgba(31,182,193,0.10)" : "#FFFFFF",
              border: `1px solid ${active ? "rgba(31,182,193,0.55)" : "rgba(12,42,64,0.12)"}`,
              cursor: "pointer", borderRadius: 12,
              transition: "all 0.18s ease",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span style={{
                width: 14, height: 14, borderRadius: "50%",
                background: "#FFFFFF",
                border: `2px solid ${active ? "#1FB6C1" : "rgba(12,42,64,0.30)"}`,
                boxShadow: active ? "inset 0 0 0 3px #1FB6C1" : "none",
              }} />
              <span className="font-mono uppercase"
                    style={{ fontSize: "0.6rem", letterSpacing: "0.2em",
                             color: active ? "#0E7A82" : "rgba(12,42,64,0.65)",
                             fontWeight: 700 }}>
                {active ? "Selected" : "Choose"}
              </span>
            </div>
            <div style={{ fontSize: "0.9rem", color: "#0C2A40", fontWeight: 500,
                          lineHeight: 1.4 }}>
              {o.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Main component                                                */
/* ────────────────────────────────────────────────────────────── */
export default function SofteningResinSizing() {
  /* ─── 1 · DEMAND + HARDNESS ─────────────────────────────── */
  const [dailyL,         setDailyL]         = useState(20000);
  const [serviceHours,   setServiceHours]   = useState(24);
  const [rawHardness,    setRawHardness]    = useState(250);
  const [targetHardness, setTargetHardness] = useState(0);

  /* ─── 2 · REGEN TRIGGER MODE (either / or) ──────────────── */
  const [triggerMode,    setTriggerMode]    = useState("time");

  // Time-based
  const [timeInterval,   setTimeInterval]   = useState("24");
  const [customHr,       setCustomHr]       = useState(24);
  const regenHours = triggerMode === "time"
    ? (timeInterval === "custom" ? Math.max(1, customHr || 1) : parseInt(timeInterval, 10))
    : 0; // computed from volume below

  // Volume-based
  const [volPerRegenL,   setVolPerRegenL]   = useState(20000);
  const effectiveRegenHours = triggerMode === "time"
    ? regenHours
    : (dailyL > 0 ? (volPerRegenL / dailyL) * 24 : 24);
  const effectiveCycleVolumeL = triggerMode === "time"
    ? dailyL * (regenHours / 24)
    : volPerRegenL;

  /* ─── 3 · RESIN DESIGN ──────────────────────────────────── */
  const [saltEff,        setSaltEff]        = useState(4.0);
  const [safetyFactor,   setSafetyFactor]   = useState(10);

  /* ─── 4 · REGEN METHOD (either / or) ────────────────────── */
  const [regenMethod,    setRegenMethod]    = useState("eductor");

  // Eductor params
  const [valveHead,      setValveHead]      = useState("f68q-6307");
  const [customDrawLpm,  setCustomDrawLpm]  = useState(4.0);
  const head = VALVE_HEADS.find((v) => v.value === valveHead) || VALVE_HEADS[0];
  const eductorDrawLpm = valveHead === "custom"
    ? Math.max(0.1, customDrawLpm || 0.1)
    : head.drawLpm;

  // Dosing params
  const [dosingLpm,      setDosingLpm]      = useState(2.0);

  const activeDrawLpm = regenMethod === "eductor" ? eductorDrawLpm : dosingLpm;

  /* ─── 5 · CYCLE TIMING ──────────────────────────────────── */
  const [backwashMin,    setBackwashMin]    = useState(10);
  const [slowRinseMin,   setSlowRinseMin]   = useState(5);
  const [fastRinseMin,   setFastRinseMin]   = useState(10);
  const [brineRefillMin, setBrineRefillMin] = useState(5);

  // Rinse flow rates — measured in L/min (operator reads off a flow
  // meter / pump curve). null = auto from resin volume at SAC defaults
  // (0.30 / 0.08 / 0.35 BV/min). Once user overrides, the value sticks.
  const [backwashLpm,  setBackwashLpm]  = useState(null);
  const [slowRinseLpm, setSlowRinseLpm] = useState(null);
  const [fastRinseLpm, setFastRinseLpm] = useState(null);
  const [flowUnit,     setFlowUnit]     = useState("lpm"); // 'lpm' | 'm3h'

  /* ─── DERIVED ───────────────────────────────────────────── */
  const hardRemoved_mgL = Math.max(0, rawHardness - targetHardness);

  // Hard floor on salt efficiency
  const saltEffOk = saltEff >= 2.0;

  // ── Lightweight resin estimate, used to derive default rinse L/min ──
  // (rinse flow does NOT affect resin sizing, so we can compute resin
  //  first and then offer SAC-typical L/min defaults that auto-scale
  //  with the column.)
  const quickResinL = useMemo(() => {
    const load_g = effectiveCycleVolumeL * hardRemoved_mgL / 1000;
    const wc     = workingCapacityFromEff(saltEff);
    return wc > 0 ? load_g * (1 + safetyFactor / 100) / wc : 0;
  }, [effectiveCycleVolumeL, hardRemoved_mgL, saltEff, safetyFactor]);

  // Effective rinse L/min — user override or auto (SAC band centre).
  const effBackwashLpm  = backwashLpm  ?? +(0.30 * quickResinL).toFixed(1);
  const effSlowRinseLpm = slowRinseLpm ?? +(0.08 * quickResinL).toFixed(1);
  const effFastRinseLpm = fastRinseLpm ?? +(0.35 * quickResinL).toFixed(1);

  const baseParams = {
    hardRemoved_mgL, saltEff, safetyFactor,
    drawLpm: activeDrawLpm,
    backwashMin, slowRinseMin, fastRinseMin, brineRefillMin,
    backwashLpm: effBackwashLpm,
    slowRinseLpm: effSlowRinseLpm,
    fastRinseLpm: effFastRinseLpm,
    serviceHours, regenHours: effectiveRegenHours, dailyL,
  };

  const o = useMemo(() => sizeSoftener({
    ...baseParams,
    cycleVolumeL: effectiveCycleVolumeL,
  }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dailyL, hardRemoved_mgL, saltEff, safetyFactor, serviceHours,
     effectiveCycleVolumeL, effectiveRegenHours,
     activeDrawLpm,
     backwashMin, slowRinseMin, fastRinseMin, brineRefillMin,
     effBackwashLpm, effSlowRinseLpm, effFastRinseLpm]);

  // Comparison set — depends on trigger mode
  const comparison = useMemo(() => {
    if (triggerMode === "time") {
      return TIME_COMPARE_HOURS.map((h) => {
        const cycleVol = dailyL * (h / 24);
        return {
          key: `${h}h`, label: `${h} h`, hours: h, isSelected: h === regenHours,
          ...sizeSoftener({ ...baseParams, cycleVolumeL: cycleVol, regenHours: h }),
        };
      });
    }
    return VOLUME_COMPARE_FRACTIONS.map((f) => {
      const vol = dailyL * f;
      const hrs = dailyL > 0 ? (vol / dailyL) * 24 : 24;
      return {
        key: `vol-${f}`,
        label: `${fmt(vol, 0)} L  (${fmt(f * 100, 0)} %)`,
        hours: hrs,
        isSelected: Math.abs(vol - volPerRegenL) < 1,
        ...sizeSoftener({ ...baseParams, cycleVolumeL: vol, regenHours: hrs }),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerMode, dailyL, regenHours, volPerRegenL,
      hardRemoved_mgL, saltEff, safetyFactor, serviceHours,
      activeDrawLpm,
      backwashMin, slowRinseMin, fastRinseMin, brineRefillMin,
      effBackwashLpm, effSlowRinseLpm, effFastRinseLpm]);

  // Sodium added to product water — ion-exchange stoichiometry:
  // every Ca²⁺ / Mg²⁺ ion removed is replaced by two Na⁺ ions, so
  // mg/L Na added ≈ 0.46 × mg/L CaCO₃ removed.
  const sodiumAdded_mgL = hardRemoved_mgL * 0.46;

  // Status bands
  const bvStatus = !o.resinVolume_L         ? undefined
                 : o.bvPerHour < 8          ? "warn"
                 : o.bvPerHour <= 40        ? "ok"
                 : o.bvPerHour <= 60        ? "warn"
                 :                            "alert";
  const bvNote   = !o.resinVolume_L ? null
                 : o.bvPerHour < 8    ? `${fmt(o.bvPerHour, 1)} BV/h is below the typical 8–40 BV/h band — resin is over-sized for this flow.`
                 : o.bvPerHour > 40   ? `${fmt(o.bvPerHour, 1)} BV/h above 40 BV/h — premature breakthrough risk. Shorten the regen interval or split into multiple columns.`
                 :                       `${fmt(o.bvPerHour, 1)} BV/h sits inside the 8–40 BV/h industry band.`;

  const sodiumStatus = sodiumAdded_mgL > 200 ? "alert"
                     : sodiumAdded_mgL > 100 ? "warn"
                     :                         "ok";
  const sodiumNote = sodiumAdded_mgL > 200
    ? `${fmt(sodiumAdded_mgL, 0)} mg/L Na added to the product. Exceeds SANS 241 (200 mg/L) — blend a portion of raw water with softened water (partial bypass) or split-stream softening to drop below the limit.`
    : sodiumAdded_mgL > 100
    ? `${fmt(sodiumAdded_mgL, 0)} mg/L Na added — within SANS 241 (200 mg/L) but high for low-sodium dietary requirements.`
    : `${fmt(sodiumAdded_mgL, 0)} mg/L Na added — well below SANS 241 (200 mg/L).`;

  // Offline-fit alert phrasing
  const offlineFitNote = serviceHours >= 24
    ? "24 h continuous service — no offline window. Use twin-alternating duty OR a sized softened-water buffer."
    : o.regenFitsOffline
      ? `${fmt(o.totalRegen_H, 2)} h regen fits the ${fmt(o.offlineWindowH, 1)} h offline window.`
      : `${fmt(o.totalRegen_H, 2)} h regen exceeds the ${fmt(o.offlineWindowH, 1)} h offline window. Pick a larger ${regenMethod === "eductor" ? "valve head" : "dosing rate"}, shorten the regen interval, or move to twin alternating duty.`;
  const offlineFitStatus = serviceHours >= 24
    ? "alert"
    : (o.regenFitsOffline ? "ok" : "warn");

  return (
    <ToolShell
      slug="softening-resin"
      title="Water softening resin sizing"
      oneLiner="Supply daily demand, hardness, regen trigger and regen method — the tool returns resin volume, salt kg, brine L, brine tank size, cycle minutes and a hard go/no-go on whether the cycle fits the offline window."
      onExport={() => ({
        inputs: [
          { label: "Daily water demand",         value: dailyL,        unit: "L/day" },
          { label: "Service hours per day",      value: serviceHours,  unit: "h/day" },
          { label: "Raw water hardness",         value: rawHardness,   unit: "mg/L as CaCO₃",
            alts: [{ value: fmt(rawHardness / 17.1, 1), unit: "gpg" }] },
          { label: "Target effluent hardness",   value: targetHardness, unit: "mg/L as CaCO₃" },
          { label: "Regeneration trigger",
            value: triggerMode === "time"
              ? `Time-based · every ${fmt(regenHours, 1)} h`
              : `Volume-based · every ${fmt(volPerRegenL, 0)} L treated` },
          { label: "Effective regen interval",   value: fmt(effectiveRegenHours, 2), unit: "h" },
          { label: "Salt-efficiency target",     value: saltEff,       unit: "g CaCO₃ / g NaCl" },
          { label: "Implied working capacity",   value: fmt(o.workingCap, 0), unit: "g CaCO₃ / L resin" },
          { label: "Safety factor",              value: safetyFactor,  unit: "%" },
          { label: "Regeneration method",
            value: regenMethod === "eductor"
              ? `Eductor — ${valveHead === "custom" ? "Custom" : head.label}`
              : "Direct chemical dosing" },
          { label: regenMethod === "eductor" ? "Brine draw rate (head)" : "Dosing pump rate",
            value: fmt(activeDrawLpm, 2), unit: "L/min" },
          { label: "Backwash time",              value: backwashMin,   unit: "min" },
          { label: "Slow rinse time",            value: slowRinseMin,  unit: "min" },
          { label: "Fast rinse time",            value: fastRinseMin,  unit: "min" },
          { label: "Brine refill time",          value: brineRefillMin, unit: "min" },
          { label: "Backwash flow",              value: fmt(effBackwashLpm, 1),   unit: "L/min",
            alts: [
              { value: fmt(effBackwashLpm * 0.06, 2), unit: "m³/h" },
              { value: fmt(quickResinL > 0 ? effBackwashLpm / quickResinL : 0, 2), unit: "BV/min" },
            ] },
          { label: "Slow rinse flow",            value: fmt(effSlowRinseLpm, 1),  unit: "L/min",
            alts: [
              { value: fmt(effSlowRinseLpm * 0.06, 2), unit: "m³/h" },
              { value: fmt(quickResinL > 0 ? effSlowRinseLpm / quickResinL : 0, 2), unit: "BV/min" },
            ] },
          { label: "Fast rinse flow",            value: fmt(effFastRinseLpm, 1),  unit: "L/min",
            alts: [
              { value: fmt(effFastRinseLpm * 0.06, 2), unit: "m³/h" },
              { value: fmt(quickResinL > 0 ? effFastRinseLpm / quickResinL : 0, 2), unit: "BV/min" },
            ] },
        ],
        results: [
          { label: "Hardness load per cycle",   value: fmt(o.loadPerCycle_g, 0), unit: "g CaCO₃" },
          { label: "Resin volume",              value: fmt(o.resinVolume_L, 0),  unit: "L",
            alts: [
              { value: fmt(o.resinMass_kg, 0), unit: "kg" },
              { value: fmt(o.resinVolume_L / 28.317, 2), unit: "ft³" },
            ] },
          { label: "Salt per regen",            value: fmt(o.saltPerRegen_kg, 2), unit: "kg NaCl" },
          { label: "Brine per regen",           value: fmt(o.brinePerRegen_L, 0), unit: "L" },
          { label: "Brine tank min size (+20%)",value: fmt(o.brineTankMinL, 0),   unit: "L" },
          { label: regenMethod === "eductor" ? "Brine draw time" : "Brine dosing time",
            value: fmt(o.brineDrawMin, 1), unit: "min" },
          { label: "Backwash water per regen",  value: fmt(o.backwashL, 0),  unit: "L" },
          { label: "Slow rinse water per regen", value: fmt(o.slowRinseL, 0), unit: "L" },
          { label: "Fast rinse water per regen", value: fmt(o.fastRinseL, 0), unit: "L" },
          { label: "Total rinse + backwash",    value: fmt(o.rinsePerRegen_L, 0), unit: "L" },
          { label: "Total regen cycle",         value: fmt(o.totalRegenMin, 0),   unit: "min",
            alts: [{ value: fmt(o.totalRegen_H, 2), unit: "h" }] },
          { label: "Daily offline window",      value: fmt(o.offlineWindowH, 1),  unit: "h" },
          { label: "Regen fits offline window",
            value: serviceHours >= 24
              ? "needs twin alternating / buffer"
              : (o.regenFitsOffline ? "yes" : "no — see fix-it note") },
          { label: "Regens per day",            value: fmt(o.regensPerDay, 2),    unit: "/day" },
          { label: "Daily salt usage",          value: fmt(o.dailySalt_kg, 2),    unit: "kg/day" },
          { label: "Daily regen water",         value: fmt(o.dailyWaste_L, 0),    unit: "L/day" },
          { label: "Service flow rate",         value: fmt(serviceHours > 0 ? dailyL / serviceHours : 0, 0), unit: "L/h" },
          { label: "Service rate (BV/h)",       value: fmt(o.bvPerHour, 1),       unit: "BV/h",
            status: bvStatus, note: bvNote },
          { label: "Sodium added to softened water", value: fmt(sodiumAdded_mgL, 1), unit: "mg/L Na",
            status: sodiumStatus, note: sodiumNote },
        ],
        notes: [
          "Load per cycle (g CaCO₃) = cycle volume × (raw − target hardness) ÷ 1000.",
          "Salt per regen (kg) = load ÷ salt efficiency (g/g) ÷ 1000.",
          "Brine per regen (L) = salt × 1000 ÷ brine strength (320 g/L saturated).",
          "Working capacity (g/L resin) ≈ 65 − 7 × salt efficiency.",
          "Resin volume (L) = load × (1 + safety factor) ÷ working capacity.",
          "Brine draw / dosing time (min) = brine volume ÷ draw / dose rate (L/min).",
          "Rinse water (L) = rinse time (min) × rinse flow rate (L/min). Defaults track the SAC bed-volume band (0.30 / 0.08 / 0.35 BV/min for backwash / slow / fast) and auto-scale with the column.",
          "Sodium added (mg/L Na) ≈ 0.46 × hardness removed (mg/L CaCO₃) — ion-exchange stoichiometry. SANS 241 limit 200 mg/L; use partial bypass blending if exceeded.",
          "Slow rinse and fast rinse apply to BOTH eductor and direct-dosing regeneration. Only the brine-application step differs (eductor venturi suction vs. dosing-pump injection).",
          "Cycle must fit (24 − service hours).  If not → larger head, shorter interval, or twin alternating duty.",
          "Reference only — HidroVerse validates against real feed water and vendor curves before issuing a procurement spec.",
        ],
        spec: o.resinVolume_L > 0 ? {
          title: "Supplier-ready softener specification",
          body: `Specify a SAC softener with <strong>${fmt(o.resinVolume_L, 0)} L (${fmt(o.resinMass_kg, 0)} kg, ${fmt(o.resinVolume_L / 28.317, 2)} ft³)</strong> of strong-acid cation resin operating at <strong>${fmt(o.workingCap, 0)} g CaCO₃ / L</strong> working capacity (salt efficiency target ${saltEff} g/g, incl. ${safetyFactor}% SF) to treat <strong>${fmt(dailyL, 0)} L/day</strong> of feed at <strong>${fmt(rawHardness, 0)} mg/L hardness</strong>. Regenerate ${triggerMode === "time"
            ? `<strong>every ${regenHours} h (time-based)</strong>`
            : `<strong>every ${fmt(volPerRegenL, 0)} L treated (volume-based, ≈ every ${fmt(effectiveRegenHours, 1)} h)</strong>`} with <strong>${fmt(o.saltPerRegen_kg, 2)} kg NaCl</strong> and <strong>${fmt(o.brinePerRegen_L, 0)} L of saturated brine</strong> (provide a brine tank ≥ <strong>${fmt(o.brineTankMinL, 0)} L</strong>). ${regenMethod === "eductor"
              ? `Use a <strong>${valveHead === "custom" ? `${fmt(activeDrawLpm, 2)} L/min eductor head` : head.label}</strong>; brine draw step runs <strong>${fmt(o.brineDrawMin, 1)} min</strong>.`
              : `Dose the brine with a pump at <strong>${fmt(activeDrawLpm, 2)} L/min</strong>; brine injection runs <strong>${fmt(o.brineDrawMin, 1)} min</strong>.`} Full regen cycle is <strong>${fmt(o.totalRegenMin, 0)} min (${fmt(o.totalRegen_H, 2)} h)</strong> ${serviceHours >= 24
                ? "— continuous service requires twin-alternating duty or a sized softened-water buffer."
                : (o.regenFitsOffline
                  ? `— fits the ${fmt(o.offlineWindowH, 1)} h daily offline window.`
                  : `— exceeds the ${fmt(o.offlineWindowH, 1)} h offline window; pick a larger ${regenMethod === "eductor" ? "head" : "dosing rate"}, shorten the interval, or move to twin alternating duty.`)} Daily consumables: <strong>${fmt(o.dailySalt_kg, 2)} kg salt</strong>, <strong>${fmt(o.dailyWaste_L, 0)} L regen water</strong>. Service rate <strong>${fmt(o.bvPerHour, 1)} BV/h</strong> ${o.bvPerHour >= 8 && o.bvPerHour <= 40 ? "is within" : "is outside"} the 8 – 40 BV/h industry band.`,
        } : null,
      })}
    >
      {/* INTRO BANNER */}
      <div className="mb-5 p-4"
           style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)",
                    borderRadius: 12 }}
           data-testid="srs-intro-banner">
        <p className="font-light leading-relaxed"
           style={{ fontSize: "0.84rem", color: "#0C2A40" }}>
          Single chain of truth: demand + hardness → load → salt → brine → resin → cycle time → offline fit. Pick whether to <em>regenerate by time</em> or <em>by treated volume</em> and whether the head <em>draws brine via an eductor</em> or <em>doses it directly</em>. The comparison table below lets you swap sizing across multiple options and see the trade-offs at a glance.
        </p>
      </div>

      {/* ─── 1 · DEMAND + HARDNESS ─────────────────────────── */}
      <SectionHeader n="1" label="Water demand &amp; hardness" />
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Daily water demand"
               hint="Total softened water required per day.">
          <SuffixNum value={dailyL} onChange={setDailyL}
                     suffix="L/day" step="100" min={0} testid="srs-daily" />
        </Field>
        <Field label="Service hours per day"
               hint="Hours per day the softener is actually in service. 24 = continuous duty — will force you to twin alternating or use a buffer.">
          <SuffixNum value={serviceHours} onChange={setServiceHours}
                     suffix="h/day" step="1" min={1} testid="srs-service-hours" />
        </Field>
        <Field label="Raw water hardness"
               hint="Expressed as CaCO₃. Convert: gpg × 17.1; German °dH × 17.85; French °f × 10.">
          <SuffixNum value={rawHardness} onChange={setRawHardness}
                     suffix="mg/L CaCO₃" step="5" min={0} testid="srs-raw-hardness" />
        </Field>
        <Field label="Target effluent hardness"
               hint="Usually 0 for full softening; higher only for partial bypass-blending.">
          <SuffixNum value={targetHardness} onChange={setTargetHardness}
                     suffix="mg/L CaCO₃" step="1" min={0} testid="srs-target-hardness" />
        </Field>
      </div>

      <div className="mt-3 px-4 py-2 flex flex-wrap items-baseline gap-3"
           style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)",
                    borderRadius: 10 }}
           data-testid="srs-load-summary">
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.2em",
                       color: "rgba(12,42,64,0.55)" }}>
          Daily hardness load trapped
        </span>
        <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0C2A40" }}>
          {fmt(dailyL * hardRemoved_mgL / 1000, 0)} g CaCO₃ / day
        </span>
        <span className="font-mono"
              style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
          ({fmt(hardRemoved_mgL, 0)} mg/L × {fmt(dailyL, 0)} L ÷ 1000)
        </span>
      </div>

      {/* ─── 2 · REGEN TRIGGER MODE (either/or) ────────────── */}
      <SectionHeader n="2" label="Regeneration trigger — pick one" topSpacing />
      <RadioPair value={triggerMode} onChange={setTriggerMode}
                 options={TRIGGER_MODES} testid="srs-trigger-mode" />

      {triggerMode === "time" && (
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <Field label="Time interval">
            <Select value={timeInterval} onChange={setTimeInterval}
                    options={TIME_PRESETS} testid="srs-time-interval" />
          </Field>
          {timeInterval === "custom" && (
            <Field label="Custom hours"
                   hint="Hours between consecutive regens.">
              <SuffixNum value={customHr} onChange={setCustomHr}
                         suffix="h" step="1" min={1} testid="srs-time-custom" />
            </Field>
          )}
        </div>
      )}

      {triggerMode === "volume" && (
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <Field label="Treated volume between regens"
                 hint="Meter-triggered. Effective interval scales with actual demand.">
            <SuffixNum value={volPerRegenL} onChange={setVolPerRegenL}
                       suffix="L" step="100" min={100} testid="srs-vol-per-regen" />
          </Field>
          <div className="px-4 py-2.5"
               style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.10)",
                        borderRadius: 10 }}>
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.2em",
                           color: "rgba(12,42,64,0.55)" }}>
              Effective interval
            </span>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0C2A40" }}>
              every {fmt(effectiveRegenHours, 2)} h
              <span className="ml-2 font-mono"
                    style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>
                = {fmt(24 / effectiveRegenHours, 2)} regens/day
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ─── 3 · RESIN DESIGN ──────────────────────────────── */}
      <SectionHeader n="3" label="Resin design — salt efficiency drives the column" topSpacing />
      <p className="font-light mb-3"
         style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.7)", lineHeight: 1.55 }}>
        Salt efficiency (g CaCO₃ removed per g NaCl) is the single design
        knob. Higher efficiency = smaller salt budget but bigger resin
        column. Working capacity follows from the vendor curve.
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Salt efficiency target"
               hint="Industry band 2.5 – 6.0 g/g. Lower than 2.0 is rejected — that's worse than the worst real-world softener.">
          <Select value={String(saltEff)}
                  onChange={(v) => setSaltEff(parseFloat(v))}
                  options={SALT_EFF_PRESETS.map((p) => ({ value: String(p.value), label: p.label }))}
                  testid="srs-salt-eff" />
        </Field>
        <Field label="Working capacity (derived)">
          <div className="px-3 py-2.5 border bg-white font-mono"
               style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                        fontSize: "0.95rem", fontWeight: 600 }}
               data-testid="srs-working-cap">
            {fmt(o.workingCap, 0)} g CaCO₃ / L
          </div>
        </Field>
        <Field label="Safety factor"
               hint="Standard 10 %. Oversizes the resin to absorb hardness spikes and resin fouling.">
          <SuffixNum value={safetyFactor} onChange={setSafetyFactor}
                     suffix="%" step="5" min={0} testid="srs-safety" />
        </Field>
      </div>

      {!saltEffOk && (
        <div className="mt-3 p-3"
             style={{ background: "rgba(184,92,56,0.08)",
                      border: "1px solid rgba(184,92,56,0.40)",
                      borderRadius: 10, color: "#A14523" }}
             data-testid="srs-salt-eff-floor">
          Salt efficiency must be ≥ 2.0 g CaCO₃/g NaCl. Below that the
          softener is using more salt than ion chemistry requires —
          equipment outside normal industry practice.
        </div>
      )}

      {/* ─── 4 · REGEN METHOD (either/or) ──────────────────── */}
      <SectionHeader n="4" label="Regeneration method — pick one" topSpacing />
      <RadioPair value={regenMethod} onChange={setRegenMethod}
                 options={REGEN_METHODS} testid="srs-regen-method" />

      {regenMethod === "eductor" && (
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <Field label="Control valve head"
                 hint="Sets the brine draw rate from the head's published injector table.">
            <Select value={valveHead} onChange={setValveHead}
                    options={VALVE_HEADS.map((v) => ({ value: v.value, label: v.label }))}
                    testid="srs-valve-head" />
          </Field>
          {valveHead === "custom" ? (
            <Field label="Custom draw rate"
                   hint="L/min of brine drawn through the resin during the brine cycle. Fleck and Clack publish equivalent tables.">
              <SuffixNum value={customDrawLpm} onChange={setCustomDrawLpm}
                         suffix="L/min" step="0.1" min={0.1} testid="srs-custom-lpm" />
            </Field>
          ) : (
            <Field label="Selected draw rate (reference)"
                   hint="Published nominal @ 0.30 MPa. Verify against your installed eductor colour/code.">
              <div className="px-3 py-2.5 border bg-white font-mono"
                   style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                            fontSize: "0.95rem", fontWeight: 600 }}
                   data-testid="srs-head-lpm">
                {fmt(eductorDrawLpm, 2)} L/min
              </div>
            </Field>
          )}
        </div>
      )}

      {regenMethod === "dosing" && (
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <Field label="Dosing pump rate"
                 hint="Litres of brine per minute the dosing pump injects into the column. Sets how long the brine cycle runs.">
            <SuffixNum value={dosingLpm} onChange={setDosingLpm}
                       suffix="L/min" step="0.1" min={0.1} testid="srs-dosing-lpm" />
          </Field>
        </div>
      )}

      {/* ─── 5 · CYCLE TIMING ──────────────────────────────── */}
      <SectionHeader n="5" label="Cycle timing &amp; rinse-flow rates" topSpacing />

      <div className="mb-3 px-4 py-3"
           style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)",
                    borderRadius: 10 }}
           data-testid="srs-rinse-explainer">
        <p className="font-light leading-relaxed"
           style={{ fontSize: "0.82rem", color: "#0C2A40" }}>
          Every modern multi-port valve head — <strong>Runxin</strong>,{" "}
          <strong>Fleck</strong>, <strong>Clack</strong>,{" "}
          <strong>Pentair Autotrol</strong> — runs the same five
          programmable steps: <em>backwash → brine + slow rinse → fast
          rinse → brine refill → service</em>. When you replace the
          eductor with a <em>direct chemical dosing pump</em>, the rinse
          cycle is unchanged — only the brine-application step swaps
          venturi suction for pump injection. Enter the rinse flow rates
          in L/min (or m³/h) the way you'd read them off a flow meter or
          pick them off a pump curve; the BV/min equivalent is shown
          beneath each input as a sanity-check against the SAC industry
          band.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Backwash time"
               hint="Factory default 10 min. Higher for turbid feeds.">
          <SuffixNum value={backwashMin} onChange={setBackwashMin}
                     suffix="min" step="1" min={0} testid="srs-cycle-backwash" />
        </Field>
        <Field label="Slow rinse time"
               hint="Slow drainage of residual brine. Typical 5–10 min. Applies to BOTH eductor and dosing modes.">
          <SuffixNum value={slowRinseMin} onChange={setSlowRinseMin}
                     suffix="min" step="1" min={0} testid="srs-cycle-slowrinse" />
        </Field>
        <Field label="Fast rinse time"
               hint="Factory default 10 min. Rinses bed at service flow. Applies to BOTH eductor and dosing modes.">
          <SuffixNum value={fastRinseMin} onChange={setFastRinseMin}
                     suffix="min" step="1" min={0} testid="srs-cycle-fastrinse" />
        </Field>
        <Field label={regenMethod === "dosing" ? "Brine refill time (0 if no brine tank)" : "Brine refill time"}
               hint={regenMethod === "dosing"
                 ? "For direct dosing the brine is prepared per batch, so no automatic refill. Set 0 unless you are still using a brine tank topped up by the valve."
                 : "Factory default 5 min. Refills the brine tank."}>
          <SuffixNum value={brineRefillMin} onChange={setBrineRefillMin}
                     suffix="min" step="1" min={0} testid="srs-cycle-refill" />
        </Field>
      </div>

      {/* Unit toggle for the rinse FLOW inputs */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono uppercase"
             style={{ fontSize: "0.6rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.6)", fontWeight: 700 }}>
          Rinse flow rates — units
        </div>
        <div className="flex items-center gap-2" data-testid="srs-flow-unit-toggle">
          {[
            { v: "lpm", label: "L/min" },
            { v: "m3h", label: "m³/h" },
          ].map((u) => {
            const active = flowUnit === u.v;
            return (
              <button
                key={u.v}
                type="button"
                onClick={() => setFlowUnit(u.v)}
                data-testid={`srs-flow-unit-${u.v}`}
                style={{
                  padding: "5px 12px",
                  background: active ? "rgba(31,182,193,0.18)" : "#FFFFFF",
                  border: `1px solid ${active ? "rgba(31,182,193,0.55)" : "rgba(12,42,64,0.16)"}`,
                  color: active ? "#0E7A82" : "rgba(12,42,64,0.7)",
                  borderRadius: 999,
                  fontSize: "0.78rem",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                }}
              >
                {u.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setBackwashLpm(null);
              setSlowRinseLpm(null);
              setFastRinseLpm(null);
            }}
            data-testid="srs-flow-reset"
            style={{
              padding: "5px 12px",
              background: "#FFFFFF",
              border: "1px solid rgba(12,42,64,0.16)",
              color: "rgba(12,42,64,0.7)",
              borderRadius: 999,
              fontSize: "0.74rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Reset to SAC defaults
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mt-2">
        <FlowField
          label="Backwash flow"
          value={effBackwashLpm}
          onChange={setBackwashLpm}
          isAuto={backwashLpm === null}
          flowUnit={flowUnit}
          resinL={quickResinL}
          bvBand="0.25 – 0.40"
          purpose="lifts and re-classifies the bed; clears trapped sediment"
          testid="srs-bw-flow" />
        <FlowField
          label="Slow rinse flow"
          value={effSlowRinseLpm}
          onChange={setSlowRinseLpm}
          isAuto={slowRinseLpm === null}
          flowUnit={flowUnit}
          resinL={quickResinL}
          bvBand="0.05 – 0.10"
          purpose="matches eductor draw rate; pushes residual brine through the bed"
          testid="srs-sr-flow" />
        <FlowField
          label="Fast rinse flow"
          value={effFastRinseLpm}
          onChange={setFastRinseLpm}
          isAuto={fastRinseLpm === null}
          flowUnit={flowUnit}
          resinL={quickResinL}
          bvBand="0.30 – 0.50"
          purpose="rinses to service quality before returning to duty"
          testid="srs-fr-flow" />
      </div>

      {/* ─── 6 · CALCULATED SIZING ─────────────────────────── */}
      <SectionHeader n="6"
                     label={`Calculated sizing — ${triggerMode === "time"
                       ? `every ${fmt(regenHours, 0)} h`
                       : `every ${fmt(volPerRegenL, 0)} L  (≈ ${fmt(effectiveRegenHours, 1)} h)`}`}
                     topSpacing />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Resin volume"
              value={fmt(o.resinVolume_L, 0)} unit="L"
              alt={`${fmt(o.resinMass_kg, 0)} kg · ${fmt(o.resinVolume_L / 28.317, 2)} ft³ · at ${fmt(o.workingCap, 0)} g/L cap, ${safetyFactor}% SF`}
              highlight testid="srs-resin-vol" />
        <Stat label="Salt per regen"
              value={fmt(o.saltPerRegen_kg, 2)} unit="kg NaCl"
              alt={`Load ${fmt(o.loadPerCycle_g, 0)} g CaCO₃ ÷ ${saltEff} g/g salt efficiency`}
              highlight testid="srs-salt-regen" />
        <Stat label="Brine per regen"
              value={fmt(o.brinePerRegen_L, 0)} unit="L"
              alt={`Saturated brine at ${BRINE_STRENGTH} g/L`}
              highlight testid="srs-brine-regen" />
        <Stat label="Brine tank min size"
              value={fmt(o.brineTankMinL, 0)} unit="L"
              alt={`Brine per regen + 20 % headspace`}
              highlight testid="srs-brine-tank" />
        <Stat label={regenMethod === "eductor" ? "Brine draw time" : "Brine dosing time"}
              value={fmt(o.brineDrawMin, 1)} unit="min"
              alt={`${fmt(o.brinePerRegen_L, 0)} L ÷ ${fmt(activeDrawLpm, 2)} L/min`}
              testid="srs-brine-draw-min" />
        <Stat label="Total regen cycle"
              value={fmt(o.totalRegenMin, 0)} unit="min"
              alt={`${fmt(o.totalRegen_H, 2)} h · backwash + brine + rinses + refill`}
              testid="srs-total-regen" />
        <Stat label="Total rinse + backwash"
              value={fmt(o.rinsePerRegen_L, 0)} unit="L"
              alt={`bw ${fmt(o.backwashL, 0)} + slow ${fmt(o.slowRinseL, 0)} + fast ${fmt(o.fastRinseL, 0)}`}
              testid="srs-total-rinse" />
        <Stat label="Daily salt usage"
              value={fmt(o.dailySalt_kg, 2)} unit="kg/day"
              alt={`${fmt(o.dailySalt_kg * 30, 0)} kg/month · ${fmt(o.dailySalt_kg * 365, 0)} kg/year`}
              testid="srs-salt-daily" />
      </div>

      {/* Offline window verdict */}
      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <Stat label="Daily offline window"
              value={fmt(o.offlineWindowH, 1)} unit="h"
              alt={serviceHours >= 24 ? "24 h service — no offline window" : `24 − ${serviceHours} h service`}
              testid="srs-offline-window" />
        <Stat label="Regen fits offline window"
              value={serviceHours >= 24
                ? "needs twin"
                : (o.regenFitsOffline ? "yes" : "no")}
              unit=""
              alt={offlineFitNote}
              status={offlineFitStatus}
              testid="srs-regen-fit" />
        <Stat label="Service rate"
              value={fmt(o.bvPerHour, 1)} unit="BV/h"
              alt={bvNote}
              status={bvStatus}
              testid="srs-bv-rate" />
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Stat label="Daily regen water"
              value={fmt(o.dailyWaste_L, 0)} unit="L/day"
              alt={`brine ${fmt(o.dailyBrine_L, 0)} + rinse ${fmt(o.dailyRinse_L, 0)} L/day`}
              testid="srs-waste-daily" />
        <Stat label="Sodium added to softened water"
              value={fmt(sodiumAdded_mgL, 1)} unit="mg/L Na"
              alt={sodiumNote}
              status={sodiumStatus}
              testid="srs-sodium" />
      </div>

      {/* Sodium-added explainer — answers "what does the mg/L Na mean?" */}
      <div className="mt-3 px-4 py-3"
           style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.10)",
                    borderRadius: 10 }}
           data-testid="srs-sodium-explainer">
        <div className="font-mono uppercase mb-1"
             style={{ fontSize: "0.6rem", letterSpacing: "0.22em",
                      color: "rgba(12,42,64,0.6)", fontWeight: 700 }}>
          Why does softening add sodium?
        </div>
        <p className="font-light leading-relaxed"
           style={{ fontSize: "0.82rem", color: "#0C2A40" }}>
          Ion-exchange chemistry swaps every Ca²⁺ / Mg²⁺ on the resin
          for two Na⁺ ions. The relative atomic weights mean roughly{" "}
          <strong>0.46 mg/L Na is added for every 1 mg/L CaCO₃</strong>{" "}
          of hardness removed. With your inputs — raw{" "}
          <Em>{fmt(rawHardness, 0)} mg/L CaCO₃</Em> to target{" "}
          <Em>{fmt(targetHardness, 0)} mg/L</Em> — that's{" "}
          <Em>{fmt(hardRemoved_mgL, 0)} mg/L × 0.46 ={" "}
          {fmt(sodiumAdded_mgL, 0)} mg/L Na</Em>{" "}
          in the softened water. SANS 241 caps drinking-water Na at{" "}
          200 mg/L. If you're over the limit, blend a portion of raw
          (untreated) water back into the softened line — partial
          bypass blending — or split-stream the softener so only the
          high-hardness service points are fully softened.
        </p>
      </div>

      {/* ─── 7 · COMPARISON TABLE ──────────────────────────── */}
      <SectionHeader n="7"
                     label={triggerMode === "time"
                       ? "Compare across regen intervals"
                       : "Compare across treated-volume thresholds"}
                     topSpacing />
      <p className="font-light mb-3"
         style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.7)", lineHeight: 1.55 }}>
        {triggerMode === "time"
          ? "Same demand, hardness and salt-efficiency target — applied at every common time interval."
          : "Same demand, hardness and salt-efficiency target — applied across treated-volume thresholds (as a % of daily demand)."}
      </p>

      <div className="overflow-x-auto" data-testid="srs-comparison-table">
        <table className="w-full text-left"
               style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ background: "#FAFAF7",
                         color: "rgba(12,42,64,0.55)",
                         borderBottom: "1px solid rgba(12,42,64,0.10)" }}>
              <Th>{triggerMode === "time" ? "Interval" : "Volume / cycle"}</Th>
              <Th>Resin (L / kg)</Th>
              <Th>Salt / regen</Th>
              <Th>Brine / regen</Th>
              <Th>Brine draw</Th>
              <Th>Total cycle</Th>
              <Th>Salt / day</Th>
              <Th>BV/h</Th>
              <Th>Fits window</Th>
            </tr>
          </thead>
          <tbody>
            {comparison.map((row) => {
              const bvOk = row.bvPerHour >= 8 && row.bvPerHour <= 40;
              return (
                <tr key={row.key}
                    data-testid={`srs-row-${row.key}`}
                    style={{
                      background: row.isSelected ? "rgba(31,182,193,0.10)" : "#FFFFFF",
                      borderBottom: "1px solid rgba(12,42,64,0.08)",
                    }}>
                  <Td bold>
                    {row.label}
                    {row.isSelected && (
                      <span className="ml-2 font-mono"
                            style={{ fontSize: "0.55rem",
                                     letterSpacing: "0.18em",
                                     textTransform: "uppercase",
                                     color: "#0E7A82" }}>
                        ← selected
                      </span>
                    )}
                  </Td>
                  <Td>{fmt(row.resinVolume_L, 0)} L · {fmt(row.resinMass_kg, 0)} kg</Td>
                  <Td>{fmt(row.saltPerRegen_kg, 2)} kg</Td>
                  <Td>{fmt(row.brinePerRegen_L, 0)} L</Td>
                  <Td>{fmt(row.brineDrawMin, 1)} min</Td>
                  <Td>{fmt(row.totalRegenMin, 0)} min</Td>
                  <Td>{fmt(row.dailySalt_kg, 2)} kg</Td>
                  <Td>
                    <span style={{
                      padding: "1px 6px", borderRadius: 3,
                      background: bvOk ? "rgba(31,182,193,0.18)" : "rgba(194,146,46,0.18)",
                      color: bvOk ? "#0E7A82" : "#8A6914",
                      fontWeight: 600, fontSize: "0.78rem",
                    }}>
                      {fmt(row.bvPerHour, 1)}
                    </span>
                  </Td>
                  <Td>
                    {serviceHours >= 24 ? (
                      <span style={{
                        padding: "1px 6px", borderRadius: 3,
                        background: "rgba(184,92,56,0.16)", color: "#A14523",
                        fontWeight: 600, fontSize: "0.78rem",
                      }}>twin / buffer</span>
                    ) : row.regenFitsOffline ? (
                      <span style={{
                        padding: "1px 6px", borderRadius: 3,
                        background: "rgba(31,182,193,0.18)", color: "#0E7A82",
                        fontWeight: 600, fontSize: "0.78rem",
                      }}>yes</span>
                    ) : (
                      <span style={{
                        padding: "1px 6px", borderRadius: 3,
                        background: "rgba(194,146,46,0.18)", color: "#8A6914",
                        fontWeight: 600, fontSize: "0.78rem",
                      }}>no</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* SUPPLIER SPEC CARD — shared primitive so screen + PDF stay in lockstep */}
      <SpecCard
        eyebrow="Supplier-ready softener specification"
        testid="srs-spec-card"
        footer="Reference only — HidroVerse will validate against actual vendor capacity curves, brine maker geometry, hydraulic layout and raw-water analysis (iron, manganese, turbidity) before issuing a procurement spec."
      >
        Specify a SAC softener with{" "}
        <SpecValue>{fmt(o.resinVolume_L, 0)} L ({fmt(o.resinMass_kg, 0)} kg, {fmt(o.resinVolume_L / 28.317, 2)} ft³)</SpecValue>{" "}
        of strong-acid cation resin operating at{" "}
        <SpecValue>{fmt(o.workingCap, 0)} g CaCO₃ / L</SpecValue>{" "}
        working capacity (salt efficiency target {saltEff} g/g, incl. {safetyFactor}% SF)
        to treat{" "}
        <SpecValue>{fmt(dailyL, 0)} L/day</SpecValue>{" "}
        of feed at{" "}
        <SpecValue>{fmt(rawHardness, 0)} mg/L hardness</SpecValue>.{" "}
        Regenerate{" "}
        {triggerMode === "time"
          ? <SpecValue>every {regenHours} h (time-based)</SpecValue>
          : <SpecValue>every {fmt(volPerRegenL, 0)} L treated (volume-based, ≈ every {fmt(effectiveRegenHours, 1)} h)</SpecValue>}
        {" "}with{" "}
        <SpecValue>{fmt(o.saltPerRegen_kg, 2)} kg NaCl</SpecValue> and{" "}
        <SpecValue>{fmt(o.brinePerRegen_L, 0)} L of saturated brine</SpecValue>{" "}
        (provide a brine tank ≥{" "}
        <SpecValue>{fmt(o.brineTankMinL, 0)} L</SpecValue>).
        {" "}
        {regenMethod === "eductor" ? (
          <>Use a{" "}
            <SpecValue>{valveHead === "custom" ? `${fmt(activeDrawLpm, 2)} L/min eductor head` : head.label}</SpecValue>; brine draw step runs{" "}
            <SpecValue>{fmt(o.brineDrawMin, 1)} min</SpecValue>.</>
        ) : (
          <>Dose the brine with a pump at{" "}
            <SpecValue>{fmt(activeDrawLpm, 2)} L/min</SpecValue>; brine injection runs{" "}
            <SpecValue>{fmt(o.brineDrawMin, 1)} min</SpecValue>.</>
        )}
        {" "}Full regen cycle is{" "}
        <SpecValue>{fmt(o.totalRegenMin, 0)} min ({fmt(o.totalRegen_H, 2)} h)</SpecValue>{" "}
        {serviceHours >= 24
          ? "— continuous service requires twin-alternating duty or a sized softened-water buffer."
          : (o.regenFitsOffline
            ? `— fits the ${fmt(o.offlineWindowH, 1)} h daily offline window.`
            : `— exceeds the ${fmt(o.offlineWindowH, 1)} h offline window; pick a larger ${regenMethod === "eductor" ? "head" : "dosing rate"}, shorten the interval, or move to twin alternating duty.`)}
        {" "}Daily consumables:{" "}
        <SpecValue>{fmt(o.dailySalt_kg, 2)} kg salt</SpecValue>,{" "}
        <SpecValue>{fmt(o.dailyWaste_L, 0)} L regen water</SpecValue>.
        {" "}Service rate{" "}
        <SpecValue>{fmt(o.bvPerHour, 1)} BV/h</SpecValue>{" "}
        {o.bvPerHour >= 8 && o.bvPerHour <= 40 ? "is within" : "is outside"} the
        8 – 40 BV/h industry band.
      </SpecCard>
    </ToolShell>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Tiny table primitives                                         */
/* ────────────────────────────────────────────────────────────── */
function Th({ children }) {
  return (
    <th className="px-3 py-2.5 font-mono uppercase text-left"
        style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                 color: "rgba(12,42,64,0.65)", fontWeight: 700,
                 whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}
function Td({ children, bold }) {
  return (
    <td className="px-3 py-2.5"
        style={{ fontSize: "0.85rem", color: "#0C2A40",
                 fontWeight: bold ? 700 : 500,
                 whiteSpace: "nowrap" }}>
      {children}
    </td>
  );
}
